/**
 * 차트 렌더링 유틸리티
 * Chart.js를 사용하여 오프스크린 캔버스에 차트를 렌더링하고
 * Fabric.js Image로 변환
 */
import { Chart, registerables } from 'chart.js';
import * as fabric from 'fabric';

Chart.register(...registerables);

// PPT 기본 폰트
const CHART_FONT_FAMILY = "'Malgun Gothic', 'Noto Sans KR', sans-serif";

// Chart.js 범례 위치 매핑 (PPT → Chart.js)
const LEGEND_POS_MAP = { b: 'bottom', t: 'top', l: 'left', r: 'right' };

// 기본 시리즈 색상 (PPT accent 색상 기반)
export const DEFAULT_CHART_COLORS = ['#4472C4', '#ED7D31', '#A5A5A5', '#FFC000', '#5B9BD5', '#70AD47'];

// 차트 타입 정의
export const CHART_TYPES = [
  { id: 'bar', label: '세로 막대' },
  { id: 'barH', label: '가로 막대' },
  { id: 'line', label: '꺾은선' },
  { id: 'area', label: '영역' },
  { id: 'scatter', label: '분산형' },
  { id: 'pie', label: '원형' },
];

// 차트 서브타입 정의
export const CHART_SUB_TYPES = {
  bar: [
    { id: 'basic', label: '기본' },
    { id: 'stacked', label: '누적' },
    { id: 'percentStacked', label: '100% 누적' },
  ],
  barH: [
    { id: 'basic', label: '기본' },
    { id: 'stacked', label: '누적' },
    { id: 'percentStacked', label: '100% 누적' },
  ],
  line: [
    { id: 'basic', label: '직선' },
    { id: 'stacked', label: '누적' },
    { id: 'percentStacked', label: '100% 누적' },
  ],
  area: [
    { id: 'basic', label: '기본' },
    { id: 'stacked', label: '누적' },
    { id: 'percentStacked', label: '100% 누적' },
  ],
  scatter: [
    { id: 'basic', label: '점만' },
    { id: 'curved', label: '곡선' },
    { id: 'straight', label: '직선' },
  ],
  pie: [
    { id: 'basic', label: '기본' },
  ],
};

// 범례 위치 옵션
export const LEGEND_POSITIONS = [
  { id: 'none', label: '없음' },
  { id: 'b', label: '하단' },
  { id: 't', label: '상단' },
  { id: 'l', label: '왼쪽' },
  { id: 'r', label: '오른쪽' },
];

/**
 * 새 차트 기본 데이터 생성
 */
export const createDefaultChartData = (chartType = 'bar') => ({
  chartType,
  chartSubType: 'basic',
  title: '새 차트',
  titleFontSize: 20,
  titleColor: '#333333',
  series: [
    {
      index: 0, order: 0, name: '계열 1', color: DEFAULT_CHART_COLORS[0],
      categories: ['항목 1', '항목 2', '항목 3', '항목 4'],
      values: [4.3, 2.5, 3.5, 4.5],
    },
    {
      index: 1, order: 1, name: '계열 2', color: DEFAULT_CHART_COLORS[1],
      categories: ['항목 1', '항목 2', '항목 3', '항목 4'],
      values: [2.4, 4.4, 1.8, 2.8],
    },
    {
      index: 2, order: 2, name: '계열 3', color: DEFAULT_CHART_COLORS[2],
      categories: ['항목 1', '항목 2', '항목 3', '항목 4'],
      values: [2, 2, 3, 5],
    },
  ],
  axes: [
    { type: 'catAx', deleted: false },
    { type: 'valAx', deleted: false },
  ],
  legend: { position: 'b' },
  dataPointColors: chartType === 'pie'
    ? DEFAULT_CHART_COLORS.slice(0, 4)
    : null,
  gapWidth: 150,
  overlap: 0,
});

/**
 * Chart.js 설정 객체 생성
 */
const buildChartConfig = (chartData) => {
  const { chartType, chartSubType, title, titleFontSize, titleColor, series, axes, legend, dataPointColors, gapWidth, overlap } = chartData;

  const labels = series.length > 0 ? series[0].categories : [];

  const typeMap = { bar: 'bar', barH: 'bar', line: 'line', area: 'line', scatter: 'scatter', pie: 'pie' };
  const chartJsType = typeMap[chartType] || 'bar';

  // percentStacked: 원시 값을 퍼센트로 변환
  let processedSeries = series;
  if (chartSubType === 'percentStacked' && chartType !== 'pie') {
    const catCount = series.length > 0 ? series[0].values.length : 0;
    const totals = Array(catCount).fill(0);
    series.forEach(s => s.values.forEach((v, i) => { totals[i] += v; }));
    processedSeries = series.map(s => ({
      ...s,
      values: s.values.map((v, i) => totals[i] > 0 ? (v / totals[i]) * 100 : 0),
    }));
  }

  // 데이터셋 구성
  let datasets;

  if (chartType === 'pie') {
    const firstSeries = processedSeries[0];
    const catCount = firstSeries?.values?.length || 0;
    let pieColors;
    if (dataPointColors && dataPointColors.length >= catCount) {
      pieColors = dataPointColors.slice(0, catCount);
    } else {
      pieColors = Array.from({ length: catCount }, (_, i) => DEFAULT_CHART_COLORS[i % DEFAULT_CHART_COLORS.length]);
    }
    datasets = [{
      data: firstSeries?.values || [],
      backgroundColor: pieColors,
      borderColor: '#ffffff',
      borderWidth: 2,
    }];
  } else {
    datasets = processedSeries.map((s) => {
      let data;
      if (chartType === 'scatter') {
        data = s.categories.map((cat, i) => {
          const xVal = parseFloat(cat);
          return { x: isNaN(xVal) ? (i + 1) : xVal, y: s.values[i] || 0 };
        });
      } else {
        data = s.values;
      }

      const dataset = {
        label: s.name || '',
        data,
        backgroundColor: chartType === 'line' ? 'transparent' : s.color,
        borderColor: s.color,
        borderWidth: 2,
      };

      if (chartType === 'area') {
        dataset.fill = true;
        dataset.backgroundColor = s.color + '40';
      }

      if (chartType === 'line' || chartType === 'area') {
        dataset.tension = chartSubType === 'curved' ? 0.4 : 0;
        dataset.pointRadius = 4;
        dataset.pointBackgroundColor = s.color;
      }

      if (chartType === 'scatter') {
        dataset.pointRadius = 5;
        dataset.pointBackgroundColor = s.color;
        dataset.showLine = chartSubType !== 'basic';
        if (chartSubType === 'curved') dataset.tension = 0.4;
      }

      return dataset;
    });
  }

  // 폰트 크기
  const titleSizePx = titleFontSize || 20;
  const tickSizePx = 11;
  const legendSizePx = 11;

  // 스케일 설정
  const scales = {};
  if (chartType !== 'pie') {
    const isHorizontal = chartType === 'barH';
    const catAxis = axes.find(a => a.type === 'catAx');
    const valAxis = axes.find(a => a.type === 'valAx');

    const gridStyle = { color: 'rgba(0, 0, 0, 0.08)', drawBorder: false, borderDash: [4, 4] };
    const tickStyle = { font: { size: tickSizePx, weight: '600', family: CHART_FONT_FAMILY }, color: '#595959', padding: 4 };

    scales.x = {
      display: isHorizontal ? !(valAxis?.deleted) : !(catAxis?.deleted),
      stacked: chartSubType === 'stacked' || chartSubType === 'percentStacked',
      grid: isHorizontal ? gridStyle : { display: false },
      ticks: { ...tickStyle },
    };
    scales.y = {
      display: isHorizontal ? !(catAxis?.deleted) : !(valAxis?.deleted),
      stacked: chartSubType === 'stacked' || chartSubType === 'percentStacked',
      grid: isHorizontal ? { display: false } : gridStyle,
      ticks: { ...tickStyle },
    };

    // 값 축을 0부터 시작
    if (['bar', 'barH', 'line', 'area'].includes(chartType)) {
      (isHorizontal ? scales.x : scales.y).beginAtZero = true;
    }
    if (chartType === 'scatter') {
      scales.x.beginAtZero = true;
      scales.y.beginAtZero = true;
      // 카테고리 문자열을 X축 라벨로 표시
      if (labels.length > 0 && labels.some(l => isNaN(parseFloat(l)))) {
        // 문자열 카테고리 → 1-based 인덱스를 라벨로 매핑
        scales.x.ticks = {
          ...scales.x.ticks,
          callback: function(value) {
            const idx = Math.round(value) - 1;
            return idx >= 0 && idx < labels.length ? labels[idx] : value;
          },
          stepSize: 1,
        };
      }
    }

    if (chartSubType === 'percentStacked') {
      const pctAxis = isHorizontal ? scales.x : scales.y;
      pctAxis.max = 100;
      pctAxis.min = 0;
      pctAxis.ticks = { ...pctAxis.ticks, callback: (value) => value + '%' };
    }
  }

  const config = {
    type: chartJsType,
    data: { labels: chartType === 'scatter' ? undefined : labels, datasets },
    options: {
      responsive: false,
      maintainAspectRatio: false,
      animation: false,
      devicePixelRatio: 2,
      layout: { padding: { top: 10, right: 16, bottom: 10, left: 10 } },
      plugins: {
        legend: {
          display: !!legend,
          position: LEGEND_POS_MAP[legend?.position] || 'bottom',
          labels: { font: { size: legendSizePx, weight: '600', family: CHART_FONT_FAMILY }, padding: 16, usePointStyle: false, boxWidth: 14, boxHeight: 14 },
        },
        title: {
          display: !!title,
          text: title || '',
          font: { size: titleSizePx, family: CHART_FONT_FAMILY, weight: 'bold' },
          color: titleColor || '#333333',
          padding: { top: 10, bottom: 16 },
        },
      },
      scales: chartType === 'pie' ? {} : scales,
      indexAxis: chartType === 'barH' ? 'y' : 'x',
    },
  };

  // barChart 전용 속성
  if (chartType === 'bar' || chartType === 'barH') {
    const gw = gapWidth ?? 150;
    config.options.categoryPercentage = 100 / (100 + gw) * 1.7;
    const ov = overlap ?? 0;
    config.options.barPercentage = ov < 0 ? (1 + ov / 100) : 0.9;
  }

  return config;
};

/**
 * 차트 데이터를 오프스크린 캔버스에 렌더링하여 data URL 반환
 */
export const renderChartToDataURL = (chartData, width, height) => {
  const offscreen = document.createElement('canvas');
  offscreen.width = width;
  offscreen.height = height;
  const ctx = offscreen.getContext('2d');

  const config = buildChartConfig(chartData);

  const chart = new Chart(ctx, config);
  chart.draw();

  const dataURL = offscreen.toDataURL('image/png');
  chart.destroy();

  return dataURL;
};

/**
 * 차트 데이터로 fabric.FabricImage 생성 (차트 추가/재렌더링 공용)
 */
export const createChartFabricImage = async (chartData, width, height) => {
  const dataURL = renderChartToDataURL(chartData, width, height);
  const img = await fabric.FabricImage.fromURL(dataURL);

  img.set({
    scaleX: width / img.width,
    scaleY: height / img.height,
    _isChart: true,
    _chartType: chartData.chartType,
    _chartSubType: chartData.chartSubType,
    _chartTitle: chartData.title,
    _chartTitleFontSize: chartData.titleFontSize || null,
    _chartTitleColor: chartData.titleColor || null,
    _chartSeries: JSON.stringify(chartData.series),
    _chartAxes: JSON.stringify(chartData.axes),
    _chartLegend: chartData.legend ? JSON.stringify(chartData.legend) : null,
    _chartDataPointColors: chartData.dataPointColors ? JSON.stringify(chartData.dataPointColors) : null,
    _chartGapWidth: chartData.gapWidth ?? null,
    _chartOverlap: chartData.overlap ?? null,
  });

  return img;
};
